'use client'

import { Button } from '@qr-front/ui/components/Button'
import { cn } from '@qr-front/ui/utils'
import { wifiNameAtom } from 'features/generator/contentFormSections/WifiForm'
import { QrScreen } from 'features/generator/QrScreen'
import { TQrScreenProps } from 'features/generator/QrScreen/QrScreen'
import { useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'

import { IsPreviewModeProvider } from '../WifiScreenContexts'
import { WifiIcon } from './WifiIcon'
import { wifiPrimaryColorAtom } from './WifiScreenState'

type TWifiScreenProps = {
	previewMode: boolean
	demoMode: boolean
}

const Header = ({ demoMode }: { demoMode: boolean }) => {
	const t = useTranslations()

	const wifiName = useAtomValue(wifiNameAtom)
	const primaryColor = useAtomValue(wifiPrimaryColorAtom)

	return (
		<>
			<div
				className="absolute top-0 h-[242px] w-full transition-colors"
				style={{ backgroundColor: primaryColor }}
			/>
			<div className="z-10 mt-6.5 flex w-full flex-col items-center gap-6">
				<div className="flex w-full flex-col items-center text-white">
					<p className="text-10">{t('generator__qr_view__wifi_title')}</p>
					<h4 className="type-heading-4 max-w-full overflow-hidden break-words text-center">
						{!demoMode && wifiName
							? wifiName
							: t('generator__qr_view__wifi_default_name')}
					</h4>
				</div>
			</div>
		</>
	)
}

export const Content = ({ previewMode, demoMode }: TWifiScreenProps) => {
	const t = useTranslations()

	return (
		<IsPreviewModeProvider value={previewMode}>
			<div className={cn('relative', demoMode && 'pointer-events-none')}>
				<div className="relative grid place-items-center gap-6">
					<Header demoMode={demoMode} />
					<div className="z-50 mx-auto h-fit w-[220px] rounded-6 bg-white p-2 shadow-generator">
						<div className="flex flex-col gap-2">
							<div className="flex size-[204px] items-center justify-center bg-light-grey-70">
								<WifiIcon />
							</div>
							<Button className="rounded-6 bg-black text-body-caption text-white hover:bg-black hover:text-white">
								{t('generator__qr_view__wifi_connect_button')}
							</Button>
							<Button
								visual="secondary"
								className="rounded-6 bg-white text-body-caption text-black ring-black hover:bg-black hover:text-white"
							>
								{t('generator__qr_view__wifi_close_button')}
							</Button>
						</div>
					</div>
				</div>
			</div>
		</IsPreviewModeProvider>
	)
}

export const WifiScreen = (props: TQrScreenProps) => (
	<QrScreen
		{...props}
		primaryColor={useAtomValue(wifiPrimaryColorAtom)}
		content={<Content {...props} />}
	/>
)
